import { useCallback } from "react";
import { drawRectCallout, getRectCalloutGeometry } from "./RectCalloutToolButton";

const TOOL_IDS = {
  SELECT: "select",
  FREEHAND: "freehand",
  ARROW: "arrow",
  RECTANGLE: "rectangle",
  ELLIPSE: "ellipse",
  CLOUD: "cloud",
  TRIANGLE: "triangle",
  LINE: "line",
  CROSS: "cross",
  TEXT: "text",
  CALLOUT: "callout",
  RECT_CALLOUT: "rect-callout",
  POLYLINE: "polyline",
  REDACT: "redact",
  HIGHLIGHT: "highlight",
  STAMP: "stamp",
  IMAGE: "image",
};

export default function useMarkupRenderer(markupImageCacheRef, queueImageEntityRender) {
  const getOrCreateImageEntityAsset = useCallback(
    (source) => {
      const normalizedSource = String(source || "").trim();
      if (!normalizedSource) {
        return null;
      }

      const existing = markupImageCacheRef.current.get(normalizedSource);
      if (existing) {
        return existing;
      }

      const image = new Image();
      const record = {
        image,
        loaded: false,
        failed: false,
      };

      record.ready = new Promise((resolve) => {
        image.onload = () => {
          record.loaded = true;
          resolve();
          queueImageEntityRender();
        };

        image.onerror = () => {
          record.failed = true;
          resolve();
          queueImageEntityRender();
        };
      });

      markupImageCacheRef.current.set(normalizedSource, record);
      image.src = normalizedSource;
      return record;
    },
    [markupImageCacheRef, queueImageEntityRender],
  );

  const hexToRgba = useCallback((hex, alpha) => {
    const clean = (hex || "").replace("#", "");
    if (clean.length !== 6) {
      return `rgba(255, 235, 59, ${alpha})`;
    }
    const r = parseInt(clean.slice(0, 2), 16);
    const g = parseInt(clean.slice(2, 4), 16);
    const b = parseInt(clean.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }, []);

  const getEntityBounds = useCallback(
    (entity, ctx) => {
      const padding = Math.max(8, (entity.penSize || 2) * 2);
      if (entity.points?.length) {
        const xs = entity.points.map((p) => p.x);
        const ys = entity.points.map((p) => p.y);
        return {
          left: Math.min(...xs) - padding,
          top: Math.min(...ys) - padding,
          right: Math.max(...xs) + padding,
          bottom: Math.max(...ys) + padding,
        };
      }

      if (entity.from && entity.to) {
        return {
          left: Math.min(entity.from.x, entity.to.x) - padding,
          top: Math.min(entity.from.y, entity.to.y) - padding,
          right: Math.max(entity.from.x, entity.to.x) + padding,
          bottom: Math.max(entity.from.y, entity.to.y) + padding,
        };
      }

      if (entity.type === TOOL_IDS.TEXT) {
        const lines = String(entity.text || "").split("\n");
        const fontSize = entity.fontSize || 16;
        ctx.save();
        ctx.font = `${fontSize}px Arial`;
        const width = Math.max(
          24,
          ...lines.map((line) => Math.ceil(ctx.measureText(line).width)),
        );
        ctx.restore();
        const height = Math.max(18, Math.ceil(lines.length * fontSize * 1.25));
        return {
          left: entity.point.x - padding / 2,
          top: entity.point.y - padding / 2,
          right: entity.point.x + width + padding / 2,
          bottom: entity.point.y + height + padding / 2,
        };
      }

      if (entity.type === TOOL_IDS.STAMP) {
        const lines = String(entity.text || "")
          .split("\n")
          .filter(Boolean);
        const titleSize = Math.max(12, (entity.penSize || 2) * 2 + 8);
        const subSize = Math.max(10, titleSize - 3);
        const lineGap = 4;
        const paddingX = 10;
        const paddingY = 8;
        ctx.save();
        const widths = lines.map((line, idx) => {
          ctx.font = `700 ${idx === 0 ? titleSize : subSize}px Arial`;
          return Math.ceil(ctx.measureText(line).width);
        });
        ctx.restore();
        const maxWidth = widths.length ? Math.max(...widths) : 40;
        const contentHeight =
          titleSize +
          Math.max(0, lines.length - 1) * (subSize + lineGap) +
          (lines.length > 1 ? lineGap : 0);
        return {
          left: entity.point.x,
          top: entity.point.y,
          right: entity.point.x + maxWidth + paddingX * 2,
          bottom: entity.point.y + contentHeight + paddingY * 2,
        };
      }

      const r = Math.max(12, (entity.penSize || 2) * 3);
      return {
        left: entity.point.x - r,
        top: entity.point.y - r,
        right: entity.point.x + r,
        bottom: entity.point.y + r,
      };
    },
    [],
  );

  const getRectCalloutTextBox = useCallback((entity) => {
    const geometry = getRectCalloutGeometry({
      from: entity.from,
      to: entity.to,
      tailEdge: entity.tailEdge,
      tailTip: entity.tailTip,
    });
    const fontSize =
      entity.fontSize ||
      Math.max(12, Math.min(24, (entity.penSize || 2) * 2 + 6));
    const paddingX = Math.max(8, Math.round(fontSize * 0.55));
    const paddingY = Math.max(6, Math.round(fontSize * 0.4));

    return {
      x: geometry.bodyRect.left + paddingX,
      y: geometry.bodyRect.top + paddingY,
      width: Math.max(
        32,
        geometry.bodyRect.right - geometry.bodyRect.left - paddingX * 2,
      ),
      height: Math.max(
        24,
        geometry.bodyRect.bottom - geometry.bodyRect.top - paddingY * 2,
      ),
      fontSize,
    };
  }, []);

  const wrapTextLines = useCallback((ctx, text, maxWidth) => {
    const paragraphs = String(text || "").split("\n");
    const lines = [];

    paragraphs.forEach((paragraph) => {
      const words = paragraph.split(/\s+/).filter(Boolean);
      if (!words.length) {
        lines.push("");
        return;
      }

      const segmentWord = (word) => {
        if (ctx.measureText(word).width <= maxWidth) {
          return [word];
        }

        const parts = [];
        let currentPart = "";
        for (const char of word) {
          const nextPart = `${currentPart}${char}`;
          if (!currentPart || ctx.measureText(nextPart).width <= maxWidth) {
            currentPart = nextPart;
          } else {
            parts.push(currentPart);
            currentPart = char;
          }
        }
        if (currentPart) {
          parts.push(currentPart);
        }
        return parts;
      };

      const tokens = words.flatMap(segmentWord);

      let currentLine = tokens[0];
      for (let i = 1; i < tokens.length; i += 1) {
        const nextLine = `${currentLine} ${tokens[i]}`;
        if (ctx.measureText(nextLine).width <= maxWidth) {
          currentLine = nextLine;
        } else {
          lines.push(currentLine);
          currentLine = tokens[i];
        }
      }
      lines.push(currentLine);
    });

    return lines;
  }, []);

  const getRectCalloutTextLayout = useCallback(
    (ctx, entity) => {
      const textBox = getRectCalloutTextBox(entity);
      const maxFontSize =
        entity.fontSize ||
        Math.max(12, Math.min(24, (entity.penSize || 2) * 2 + 6));
      let fontSize = maxFontSize;

      while (fontSize >= 8) {
        ctx.font = `${fontSize}px Arial`;
        const lineHeight = Math.max(12, Math.round(fontSize * 1.2));
        const lines = wrapTextLines(ctx, entity.text, textBox.width);
        if (lines.length * lineHeight <= textBox.height) {
          return {
            ...textBox,
            fontSize,
            lineHeight,
            lines,
            totalHeight: lines.length * lineHeight,
          };
        }
        fontSize -= 1;
      }

      const minimumFontSize = 8;
      ctx.font = `${minimumFontSize}px Arial`;
      const lineHeight = Math.max(12, Math.round(minimumFontSize * 1.2));
      const lines = wrapTextLines(ctx, entity.text, textBox.width);
      return {
        ...textBox,
        fontSize: minimumFontSize,
        lineHeight,
        lines,
        totalHeight: lines.length * lineHeight,
      };
    },
    [getRectCalloutTextBox, wrapTextLines],
  );

  const drawEntityPrimitive = useCallback(
    (ctx, entity) => {
      ctx.save();
      ctx.strokeStyle = entity.penColor || "#ff2e2e";
      ctx.fillStyle = entity.penColor || "#ff2e2e";
      ctx.lineWidth = entity.penSize || 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";

      if (
        entity.type === TOOL_IDS.FREEHAND ||
        entity.type === TOOL_IDS.POLYLINE
      ) {
        const points = entity.points || [];
        if (points.length > 1) {
          ctx.beginPath();
          ctx.moveTo(points[0].x, points[0].y);
          for (let i = 1; i < points.length; i += 1) {
            ctx.lineTo(points[i].x, points[i].y);
          }
          ctx.stroke();
        }
      } else if (entity.type === TOOL_IDS.HIGHLIGHT) {
        const points = entity.points || [];
        if (points.length > 1) {
          ctx.strokeStyle = hexToRgba(entity.penColor, 0.35);
          ctx.lineWidth = Math.max(8, (entity.penSize || 2) * 3);
          ctx.beginPath();
          ctx.moveTo(points[0].x, points[0].y);
          for (let i = 1; i < points.length; i += 1) {
            ctx.lineTo(points[i].x, points[i].y);
          }
          ctx.stroke();
        }
      } else if (entity.type === TOOL_IDS.ARROW) {
        const from = entity.from;
        const to = entity.to;
        const headLength = Math.max(12, (entity.penSize || 2) * 4);
        const headWidth = Math.max(8, (entity.penSize || 2) * 2.4);
        const angle = Math.atan2(to.y - from.y, to.x - from.x);

        // Draw shaft only to the head base so the tip always sits exactly at `to`.
        const baseX = to.x - headLength * Math.cos(angle);
        const baseY = to.y - headLength * Math.sin(angle);

        ctx.beginPath();
        ctx.moveTo(from.x, from.y);
        ctx.lineTo(baseX, baseY);
        ctx.stroke();

        const normalX = Math.sin(angle);
        const normalY = -Math.cos(angle);
        const leftX = baseX + (headWidth / 2) * normalX;
        const leftY = baseY + (headWidth / 2) * normalY;
        const rightX = baseX - (headWidth / 2) * normalX;
        const rightY = baseY - (headWidth / 2) * normalY;

        ctx.beginPath();
        ctx.moveTo(to.x, to.y);
        ctx.lineTo(leftX, leftY);
        ctx.lineTo(rightX, rightY);
        ctx.lineTo(to.x, to.y);
        ctx.fill();
      } else if (entity.type === TOOL_IDS.RECTANGLE) {
        const left = Math.min(entity.from.x, entity.to.x);
        const top = Math.min(entity.from.y, entity.to.y);
        const width = Math.abs(entity.to.x - entity.from.x);
        const height = Math.abs(entity.to.y - entity.from.y);
        if (entity.fillEnabled) {
          ctx.fillStyle = entity.fillColor || entity.penColor;
          ctx.fillRect(left, top, width, height);
        }
        ctx.strokeRect(
          entity.from.x,
          entity.from.y,
          entity.to.x - entity.from.x,
          entity.to.y - entity.from.y,
        );
      } else if (entity.type === TOOL_IDS.TRIANGLE) {
        const left = Math.min(entity.from.x, entity.to.x);
        const top = Math.min(entity.from.y, entity.to.y);
        const right = Math.max(entity.from.x, entity.to.x);
        const bottom = Math.max(entity.from.y, entity.to.y);
        const points = [
          { x: (left + right) / 2, y: top },
          { x: right, y: bottom },
          { x: left, y: bottom },
        ];

        ctx.beginPath();
        ctx.moveTo(points[0].x, points[0].y);
        ctx.lineTo(points[1].x, points[1].y);
        ctx.lineTo(points[2].x, points[2].y);
        ctx.closePath();
        if (entity.fillEnabled) {
          ctx.fillStyle = entity.fillColor || entity.penColor;
          ctx.fill();
        }
        ctx.stroke();
      } else if (entity.type === TOOL_IDS.LINE) {
        ctx.beginPath();
        ctx.moveTo(entity.from.x, entity.from.y);
        ctx.lineTo(entity.to.x, entity.to.y);
        ctx.stroke();
      } else if (entity.type === TOOL_IDS.CROSS) {
        const left = Math.min(entity.from.x, entity.to.x);
        const top = Math.min(entity.from.y, entity.to.y);
        const right = Math.max(entity.from.x, entity.to.x);
        const bottom = Math.max(entity.from.y, entity.to.y);
        ctx.beginPath();
        ctx.moveTo(left, top);
        ctx.lineTo(right, bottom);
        ctx.moveTo(right, top);
        ctx.lineTo(left, bottom);
        ctx.stroke();
      } else if (entity.type === TOOL_IDS.RECT_CALLOUT) {
        drawRectCallout({
          ctx,
          from: entity.from,
          to: entity.to,
          tailEdge: entity.tailEdge,
          tailTip: entity.tailTip,
          fillEnabled: entity.fillEnabled,
          fillColor: entity.fillColor || entity.penColor,
        });
        if (entity.text) {
          const layout = getRectCalloutTextLayout(ctx, entity);
          const startY =
            layout.y + Math.max(0, (layout.height - layout.totalHeight) / 2);
          ctx.fillStyle = entity.penColor;
          ctx.font = `${layout.fontSize}px Arial`;
          ctx.textBaseline = "top";
          ctx.textAlign = "center";

          layout.lines.forEach((line, idx) => {
            ctx.fillText(
              line,
              layout.x + layout.width / 2,
              startY + idx * layout.lineHeight,
            );
          });
        }
      } else if (entity.type === TOOL_IDS.ELLIPSE) {
        const cx = (entity.from.x + entity.to.x) / 2;
        const cy = (entity.from.y + entity.to.y) / 2;
        const rx = Math.abs(entity.to.x - entity.from.x) / 2;
        const ry = Math.abs(entity.to.y - entity.from.y) / 2;
        ctx.beginPath();
        ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
        if (entity.fillEnabled) {
          ctx.fillStyle = entity.fillColor || entity.penColor;
          ctx.fill();
        }
        ctx.stroke();
      } else if (entity.type === TOOL_IDS.CLOUD) {
        const left = Math.min(entity.from.x, entity.to.x);
        const top = Math.min(entity.from.y, entity.to.y);
        const right = Math.max(entity.from.x, entity.to.x);
        const bottom = Math.max(entity.from.y, entity.to.y);
        const bump = Math.max(10, (entity.penSize || 2) * 4);
        const radius = bump / 2;
        ctx.beginPath();
        for (let x = left; x <= right; x += bump) {
          const px = Math.min(x + radius, right);
          ctx.moveTo(px - radius, top);
          ctx.arc(px, top, radius, Math.PI, 0, false);
        }
        for (let y = top; y <= bottom; y += bump) {
          const py = Math.min(y + radius, bottom);
          ctx.moveTo(right, py - radius);
          ctx.arc(right, py, radius, -Math.PI / 2, Math.PI / 2, false);
        }
        for (let x = right; x >= left; x -= bump) {
          const px = Math.max(x - radius, left);
          ctx.moveTo(px + radius, bottom);
          ctx.arc(px, bottom, radius, 0, Math.PI, false);
        }
        for (let y = bottom; y >= top; y -= bump) {
          const py = Math.max(y - radius, top);
          ctx.moveTo(left, py + radius);
          ctx.arc(left, py, radius, Math.PI / 2, -Math.PI / 2, false);
        }
        ctx.closePath();
        if (entity.fillEnabled) {
          ctx.fillStyle = entity.fillColor || entity.penColor;
          ctx.fill();
        }
        ctx.stroke();
      } else if (entity.type === TOOL_IDS.IMAGE) {
        const left = Math.min(entity.from?.x || 0, entity.to?.x || 0);
        const top = Math.min(entity.from?.y || 0, entity.to?.y || 0);
        const width = Math.abs((entity.to?.x || 0) - (entity.from?.x || 0));
        const height = Math.abs((entity.to?.y || 0) - (entity.from?.y || 0));

        if (width > 0 && height > 0) {
          const imageAsset = getOrCreateImageEntityAsset(
            entity.imageDataUrl || entity.imageSrc,
          );

          if (imageAsset?.loaded && imageAsset.image) {
            ctx.drawImage(imageAsset.image, left, top, width, height);
          } else {
            ctx.save();
            ctx.fillStyle = "rgba(255, 255, 255, 0.75)";
            ctx.strokeStyle = "rgba(13, 110, 253, 0.85)";
            ctx.lineWidth = 1.2;
            ctx.setLineDash([6, 3]);
            ctx.fillRect(left, top, width, height);
            ctx.strokeRect(left, top, width, height);
            ctx.setLineDash([]);
            ctx.fillStyle = "#0d6efd";
            ctx.font = "600 12px Arial";
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillText(
              imageAsset?.failed ? "Image failed" : "Loading image...",
              left + width / 2,
              top + height / 2,
            );
            ctx.restore();
          }
        }
      } else if (entity.type === TOOL_IDS.REDACT) {
        const left = Math.min(entity.from.x, entity.to.x);
        const top = Math.min(entity.from.y, entity.to.y);
        const width = Math.abs(entity.to.x - entity.from.x);
        const height = Math.abs(entity.to.y - entity.from.y);
        ctx.fillStyle = "rgba(0, 0, 0, 0.92)";
        ctx.fillRect(left, top, width, height);
      } else if (entity.type === TOOL_IDS.CALLOUT) {
        const radius = Math.max(10, (entity.penSize || 2) * 3);
        ctx.beginPath();
        ctx.arc(entity.point.x, entity.point.y, radius, 0, Math.PI * 2);
        ctx.fillStyle = entity.fillEnabled
          ? entity.fillColor || entity.penColor
          : "rgba(255, 255, 255, 0.96)";
        ctx.fill();
        ctx.lineWidth = Math.max(2, (entity.penSize || 2) * 0.9);
        ctx.strokeStyle = entity.penColor;
        ctx.stroke();
        ctx.fillStyle = entity.penColor;
        ctx.font = `700 ${Math.max(12, radius)}px Arial`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(
          String(entity.number || 1),
          entity.point.x,
          entity.point.y + 0.5,
        );
      } else if (entity.type === TOOL_IDS.TEXT) {
        const lines = String(entity.text || "").split("\n");
        const fontSize = entity.fontSize || 16;
        ctx.font = `${fontSize}px Arial`;
        ctx.textAlign = "left";
        ctx.textBaseline = "top";
        const lineHeight = Math.max(16, Math.round(fontSize * 1.25));

        if (entity.fillEnabled) {
          const textWidth = lines.reduce(
            (max, line) => Math.max(max, ctx.measureText(line).width),
            0,
          );
          const textHeight = Math.max(lineHeight, lines.length * lineHeight);
          const paddingX = 4;
          const paddingY = 2;
          ctx.fillStyle = entity.fillColor || entity.penColor;
          ctx.fillRect(
            entity.point.x - paddingX,
            entity.point.y - paddingY,
            Math.ceil(textWidth + paddingX * 2),
            Math.ceil(textHeight + paddingY * 2),
          );
        }

        ctx.fillStyle = entity.penColor;
        lines.forEach((line, idx) => {
          ctx.fillText(line, entity.point.x, entity.point.y + idx * lineHeight);
        });
      } else if (entity.type === TOOL_IDS.STAMP) {
        const lines = String(entity.text || "")
          .split("\n")
          .filter(Boolean);
        if (lines.length) {
          const titleSize = Math.max(12, (entity.penSize || 2) * 2 + 8);
          const subSize = Math.max(10, titleSize - 3);
          const lineGap = 4;
          const paddingX = 10;
          const paddingY = 8;
          ctx.textBaseline = "top";
          const widths = lines.map((line, idx) => {
            ctx.font = `700 ${idx === 0 ? titleSize : subSize}px Arial`;
            return Math.ceil(ctx.measureText(line).width);
          });
          const maxWidth = Math.max(...widths);
          const contentHeight =
            titleSize +
            (lines.length - 1) * (subSize + lineGap) +
            (lines.length > 1 ? lineGap : 0);
          const left = entity.point.x;
          const top = entity.point.y;
          ctx.fillStyle = "rgba(255,255,255,0.95)";
          ctx.strokeStyle = entity.penColor;
          ctx.lineWidth = Math.max(1.5, (entity.penSize || 2) * 0.5);
          ctx.beginPath();
          ctx.rect(
            left,
            top,
            maxWidth + paddingX * 2,
            contentHeight + paddingY * 2,
          );
          ctx.fill();
          ctx.stroke();
          let y = top + paddingY;
          lines.forEach((line, idx) => {
            const size = idx === 0 ? titleSize : subSize;
            const weight = idx === 0 ? 700 : 500;
            ctx.font = `${weight} ${size}px Arial`;
            ctx.fillStyle = idx === 0 ? entity.penColor : "#333";
            ctx.fillText(line, left + paddingX, y);
            y += size + lineGap;
          });
        }
      }

      ctx.restore();
    },
    [
      getOrCreateImageEntityAsset,
      getRectCalloutTextLayout,
      hexToRgba,
    ],
  );

  return {
    getOrCreateImageEntityAsset,
    getEntityBounds,
    getRectCalloutTextBox,
    drawEntityPrimitive,
  };
}
